import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    Alert
} from '@mui/material';
import {
    CheckCircle,
    Warning,
    Send,
    Cancel,
    Assignment,
    Timer,
    RateReview
} from '@mui/icons-material';
import { tokens } from '../../../../theme';

const ReviewSubmissionModal = ({
    open,
    onClose,
    onConfirm,
    reviewCount,
    totalQuestions,
    session
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    const isComplete = reviewCount === totalQuestions;
    const completionPercentage = Math.round((reviewCount / totalQuestions) * 100);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <Send color="primary" />
                    <Typography variant="h5">
                        Submit Review Session
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ mt: 2 }}>
                {/* Session Info */}
                <Box mb={3}>
                    <Typography variant="h6" gutterBottom color={colors.greenAccent[400]}>
                        Session: {session?.title || 'Review Session'}
                    </Typography>
                    {session?.description && (
                        <Typography variant="body2" color="text.secondary">
                            {session.description}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Review Summary */}
                <Typography variant="h6" gutterBottom>
                    Review Summary
                </Typography>
                
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <Assignment color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Questions Reviewed"
                            secondary={`${reviewCount} out of ${totalQuestions} (${completionPercentage}%)`}
                        />
                    </ListItem>
                    
                    <ListItem>
                        <ListItemIcon>
                            {isComplete ? (
                                <CheckCircle color="success" />
                            ) : (
                                <Warning color="warning" />
                            )}
                        </ListItemIcon>
                        <ListItemText
                            primary="Completion Status"
                            secondary={isComplete ? "All questions reviewed" : `${totalQuestions - reviewCount} questions remaining`}
                        />
                    </ListItem>
                    
                    {session?.expires_at && (
                        <ListItem>
                            <ListItemIcon>
                                <Timer color="info" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Session Expires"
                                secondary={new Date(session.expires_at).toLocaleString()}
                            />
                        </ListItem>
                    )}
                </List>

                {/* Warnings */}
                {!isComplete && session?.require_all_reviews && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        This session requires all questions to be reviewed before submission.
                        Please complete the remaining {totalQuestions - reviewCount} questions.
                    </Alert>
                )}
                
                {!isComplete && !session?.require_all_reviews && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        You have not reviewed all questions. You can still submit, but 
                        unreviewed questions will not have your feedback.
                    </Alert>
                )}

                {isComplete && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        All questions have been reviewed! Your feedback is ready to be submitted.
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Important Notice */}
                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Important Notice:
                    </Typography>
                    <Typography variant="body2">
                        You can reopen this session from the Teacher Reviews list to view or edit
                        your reviews at any time until the session expires.
                    </Typography>
                </Alert>
            </DialogContent>
            
            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    startIcon={<Cancel />}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="success"
                    startIcon={<Send />}
                    disabled={!isComplete && session?.require_all_reviews}
                >
                    {isComplete ? 'Submit All Reviews' : `Submit ${reviewCount} Reviews`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewSubmissionModal;